import { useCallback, useMemo, useState } from 'react';

import { notification } from 'antd';

import BarChartService from '@src/services/BarChartService';
import ChartService from '@src/services/ChartService';
import CountChartService from '@src/services/CountChartService';
import CounterChartService from '@src/services/CounterChartService';
import PieChartService from '@src/services/PieChartService';
import TableService from '@src/services/TableService';

import { DASHBOARDS_FORMS } from '../screens/EventsDashboard/constants';

export const useFormHandlers = ({
  boardSelected,
  orgId,
  intl,
  onRefreshCharts,
  onCloseMainDrawer,
}) => {
  const chartService = useMemo(() => new ChartService(), []);
  const tableService = useMemo(() => new TableService(), []);
  const pieChartService = useMemo(() => new PieChartService(), []);
  const countChartService = useMemo(() => new CountChartService(), []);
  const counterChartService = useMemo(() => new CounterChartService(), []);
  const barChartService = useMemo(() => new BarChartService(), []);

  const [loading, setLoading] = useState(false);

  const onFormFinish = useCallback(
    (name, { values }) => {
      if (!boardSelected.id) throw new Error('No board selected');

      const action = {
        [DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS]: async () => {
          setLoading(true);

          try {
            const formData = values?.formData;
            if (!formData) throw new Error('No form data provided');
            const chartId = formData.id;
            if (chartId) {
              await chartService.updateChart({
                data: formData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId,
              });
            } else {
              await chartService.createChart({
                data: formData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }
            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.EVENT_TABLE]: async () => {
          setLoading(true);

          try {
            const tableData = {
              ...values.tableFormData,
              id: values?.id,
            };

            if (!tableData) throw new Error('No data provided');
            const tableId = tableData?.id;

            if (tableId) {
              await tableService.updateTable({
                data: tableData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                tableId,
              });
            } else {
              await tableService.createTable({
                data: tableData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }

            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.EVENT_COUNTER]: async () => {
          setLoading(true);
          try {
            const pieChartData = {
              ...values.chartFormData,
              id: values?.id,
            };
            const chartId = pieChartData?.id;

            if (chartId) {
              await pieChartService.updatePieChart({
                data: pieChartData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId,
              });
            } else {
              await pieChartService.createPieChart({
                data: pieChartData,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }

            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME]: async () => {
          const { metadata, ...data } = values;
          const body = {
            ...data,
            layout: data.layout,
            queryParams: {
              formId: data?.formId ?? undefined,
              eventTypes: data?.eventIds ?? undefined,
              secondDimension: data?.secondDimension ?? undefined,
            },
            title: data?.title,
            aggregations: data?.aggregations,
          };

          setLoading(true);
          try {
            const countChartId = body?.id;
            if (countChartId) {
              await countChartService.updateCountChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId: countChartId,
              });
            } else {
              await countChartService.createCountChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }
            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY]: async () => {
          setLoading(true);

          try {
            const body = {
              ...values.counterFormData,
              id: values?.id,
            };
            const chartId = body?.id;
            if (chartId) {
              await counterChartService.updateCounterChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId,
              });
            } else {
              await counterChartService.createCounterChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }
            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },

        [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE]: async () => {
          setLoading(true);
          try {
            const body = {
              ...values.formData,
              id: values?.id,
            };
            const chartId = body?.id;
            if (chartId) {
              await barChartService.updateBarChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId,
              });
            } else {
              await barChartService.createBarChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }
            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },

        [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST]: async () => {
          setLoading(true);
          try {
            const chartId = values?.id;

            if (chartId) {
              await chartService.updateChart({
                data: {
                  ...values.formData,
                  id: values?.id,
                },
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId,
              });
            } else {
              await chartService.createChart({
                data: {
                  ...values.formData,
                  id: values?.id,
                },
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }

            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE]: async () => {
          setLoading(true);
          try {
            const body = {
              ...values.formData,
              id: values?.id,
            };
            const bodyId = body?.id;

            if (bodyId) {
              await tableService.updateTable({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                tableId: bodyId,
              });
            } else {
              await tableService.createTable({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }

            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
        [DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT]: async () => {
          setLoading(true);
          try {
            const body = {
              ...values.formData,
              id: values?.id,
            };
            const bodyId = body?.id;

            if (bodyId) {
              await counterChartService.updateCounterChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
                chartId: bodyId,
              });
            } else {
              await counterChartService.createCounterChart({
                data: body,
                orgId,
                divId: boardSelected?.divId,
                boardId: boardSelected.id,
              });
            }
            notification.success({
              message: intl.formatMessage({ id: 'general.save.successful.message' }),
            });
            onRefreshCharts(true);
            onCloseMainDrawer();
          } catch (error) {
            notification.error({
              message: error.message || intl.formatMessage({ id: 'general.save.error.message' }),
            });
          } finally {
            setLoading(false);
          }
        },
      };

      action[name]();
    },
    [
      boardSelected?.divId,
      boardSelected.id,
      chartService,
      intl,
      onCloseMainDrawer,
      onRefreshCharts,
      orgId,
      pieChartService,
      tableService,
      countChartService,
      counterChartService,
      barChartService,
    ],
  );

  return { loading, onFormFinish };
};
